import React from "react";
import { SxProps, Theme } from "@mui/material";
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import ManualAddressTextField from "./ManualAddressTextField";
import { getNestedFieldErrors } from "./companyAddressFormUtils";

interface ManualCountryAddressFormProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors;
  watch: UseFormWatch<Record<string, unknown>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  fieldPrefix: string;
  idPrefix?: string;
  inputSx?: SxProps<Theme>;
  size?: "small" | "medium";
}

const getAddressErrors = (
  errors: FieldErrors,
  fieldPrefix: string
): FieldErrors | undefined => getNestedFieldErrors(errors, fieldPrefix);

export const UgandaCompanyAddressForm: React.FC<
  ManualCountryAddressFormProps
> = ({ control, errors, fieldPrefix, idPrefix = "", inputSx, size }) => {
  const { t } = useKivunovaTranslation();
  const addressErrors = getAddressErrors(errors, fieldPrefix);

  return (
    <>
      <ManualAddressTextField
        name={`${fieldPrefix}.region`}
        control={control}
        label={t("region", "Region")}
        required
        error={addressErrors?.region}
        testId={idPrefix ? `${idPrefix}region` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.district`}
        control={control}
        label={t("district", "District")}
        required
        error={addressErrors?.district}
        testId={idPrefix ? `${idPrefix}district` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.city`}
        control={control}
        label={t("city-municipality", "City / Municipality")}
        error={addressErrors?.city}
        testId={idPrefix ? `${idPrefix}city` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.subCounty`}
        control={control}
        label={t("sub-county-division", "Sub-county / Division")}
        required
        error={addressErrors?.subCounty}
        testId={idPrefix ? `${idPrefix}subCounty` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.parish`}
        control={control}
        label={t("parish-ward", "Parish / Ward")}
        error={addressErrors?.parish}
        testId={idPrefix ? `${idPrefix}parish` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.village`}
        control={control}
        label={t("village", "Village")}
        error={addressErrors?.village}
        testId={idPrefix ? `${idPrefix}village` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.road`}
        control={control}
        label={t("road", "Road")}
        error={addressErrors?.road}
        testId={idPrefix ? `${idPrefix}road` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.poBox`}
        control={control}
        label={t("po-box", "P.O. Box")}
        error={addressErrors?.poBox}
        testId={idPrefix ? `${idPrefix}poBox` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.postalCode`}
        control={control}
        label={t("postal-code", "Postal code")}
        error={addressErrors?.postalCode}
        testId={idPrefix ? `${idPrefix}postalCode` : undefined}
        inputSx={inputSx}
        size={size}
      />
    </>
  );
};

export const KenyaCompanyAddressForm: React.FC<
  ManualCountryAddressFormProps
> = ({ control, errors, fieldPrefix, idPrefix = "", inputSx, size }) => {
  const { t } = useKivunovaTranslation();
  const addressErrors = getAddressErrors(errors, fieldPrefix);

  return (
    <>
      <ManualAddressTextField
        name={`${fieldPrefix}.county`}
        control={control}
        label={t("county", "County")}
        required
        error={addressErrors?.county}
        testId={idPrefix ? `${idPrefix}county` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.constituency`}
        control={control}
        label={t("constituency", "Constituency")}
        required
        error={addressErrors?.constituency}
        testId={idPrefix ? `${idPrefix}constituency` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.ward`}
        control={control}
        label={t("ward", "Ward")}
        error={addressErrors?.ward}
        testId={idPrefix ? `${idPrefix}ward` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.town`}
        control={control}
        label={t("town-city", "Town / City")}
        required
        error={addressErrors?.town}
        testId={idPrefix ? `${idPrefix}town` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.estateStreet`}
        control={control}
        label={t("estate-building-street", "Estate / Building / Street")}
        error={addressErrors?.estateStreet}
        testId={idPrefix ? `${idPrefix}estateStreet` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.poBox`}
        control={control}
        label={t("po-box", "P.O. Box")}
        error={addressErrors?.poBox}
        testId={idPrefix ? `${idPrefix}poBox` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.postalCode`}
        control={control}
        label={t("postal-code", "Postal code")}
        error={addressErrors?.postalCode}
        testId={idPrefix ? `${idPrefix}postalCode` : undefined}
        inputSx={inputSx}
        size={size}
      />
    </>
  );
};

export const TanzaniaCompanyAddressForm: React.FC<
  ManualCountryAddressFormProps
> = ({ control, errors, fieldPrefix, idPrefix = "", inputSx, size }) => {
  const { t } = useKivunovaTranslation();
  const addressErrors = getAddressErrors(errors, fieldPrefix);

  return (
    <>
      <ManualAddressTextField
        name={`${fieldPrefix}.region`}
        control={control}
        label={t("region", "Region")}
        required
        error={addressErrors?.region}
        testId={idPrefix ? `${idPrefix}region` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.district`}
        control={control}
        label={t("district", "District")}
        required
        error={addressErrors?.district}
        testId={idPrefix ? `${idPrefix}district` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.ward`}
        control={control}
        label={t("ward", "Ward")}
        required
        error={addressErrors?.ward}
        testId={idPrefix ? `${idPrefix}ward` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.streetVillageHamlet`}
        control={control}
        label={t("street-village-hamlet", "Street / Village / Hamlet")}
        error={addressErrors?.streetVillageHamlet}
        testId={idPrefix ? `${idPrefix}streetVillageHamlet` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.houseNumber`}
        control={control}
        label={t("house-number", "House number")}
        error={addressErrors?.houseNumber}
        testId={idPrefix ? `${idPrefix}houseNumber` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.poBox`}
        control={control}
        label={t("po-box", "P.O. Box")}
        error={addressErrors?.poBox}
        testId={idPrefix ? `${idPrefix}poBox` : undefined}
        inputSx={inputSx}
        size={size}
      />
      <ManualAddressTextField
        name={`${fieldPrefix}.postalCode`}
        control={control}
        label={t("postal-code", "Postal code")}
        error={addressErrors?.postalCode}
        testId={idPrefix ? `${idPrefix}postalCode` : undefined}
        inputSx={inputSx}
        size={size}
      />
    </>
  );
};

// Keep setValue/watch in signature for API consistency with Rwanda form
export type CountryAddressFormProps = ManualCountryAddressFormProps;
